import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Download, Mail, Instagram, Music } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const PressContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const { language } = useLanguage();
  const t = translations[language];
  const config = useConfig();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t.toasts.nameRequired);
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error(t.toasts.emailRequired);
      return;
    }
    
    if (!formData.email.includes('@')) {
      toast.error(t.toasts.emailInvalid);
      return;
    }
    
    if (!formData.message.trim()) {
      toast.error(t.toasts.messageRequired);
      return;
    }
    
    toast.success(t.toasts.messageSuccess);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-accent text-sm font-semibold tracking-widest uppercase">{t.contact.subtitle}</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6">
            {t.contact.title.split('&')[0]}& <span className="text-glow-soul" style={{ color: 'hsl(186 100% 42%)' }}>{t.contact.title.split('&')[1]}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Press Information */}
          <div className="space-y-8">
            <Card className="p-8 bg-card/30 backdrop-blur border-border/50">
              <h3 className="text-2xl font-bold mb-4 text-primary">{t.contact.pressInfo.title}</h3>
              
              <blockquote className="text-foreground/90 mb-6 border-l-2 border-primary/50 pl-4 italic">
                "{t.contact.pressInfo.quote}"
              </blockquote>

              <div className="space-y-4">
                <Button
                  variant="outline"
                  className="w-full justify-start border-primary/50 text-primary hover:bg-primary/10 divine-glow"
                  onClick={() => window.open(config.links.pressKitDownload, '_blank')}
                >
                  <Download className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">{t.contact.pressInfo.downloadKit}</span>
                </Button>

                <div className="pt-4 space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <div className="text-xs text-muted-foreground">{t.contact.pressInfo.booking}</div>
                      <a
                        href={`mailto:${config.links.bookingEmail}`}
                        className="text-foreground hover:text-primary transition-colors"
                      >
                        {config.links.bookingEmail}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Music className="h-5 w-5 text-primary" />
                    <div>
                      <div className="text-xs text-muted-foreground">{t.contact.pressInfo.label}</div>
                      <a
                        href={config.links.labelWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground hover:text-primary transition-colors"
                      >
                        Moonlight Reworks
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Social Links */}
            <Card className="p-8 bg-card/30 backdrop-blur border-border/50">
              <h3 className="text-2xl font-bold mb-4 text-primary">{t.contact.followUs}</h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start border-secondary/50 text-secondary hover:bg-secondary/10 fire-glow"
                  onClick={() => window.open(config.links.instagram, '_blank')}
                >
                  <Instagram className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">Instagram</span>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start border-accent/50 text-accent hover:bg-accent/10 soul-glow"
                  onClick={() => window.open(config.links.tiktok, '_blank')}
                >
                  <Music className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">TikTok</span>
                </Button>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="p-8 bg-card/30 backdrop-blur border-border/50">
            <h3 className="text-2xl font-bold mb-6 text-primary">{t.contact.contactForm.title}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.contactForm.nameLabel}
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-background/50 border-border/50 focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.contactForm.emailLabel}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-background/50 border-border/50 focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.contactForm.messageLabel}
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-background/50 border-border/50 focus:border-primary resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full divine-glow bg-primary text-primary-foreground hover:bg-primary/90"
                size="lg"
              >
                {t.contact.contactForm.send}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PressContact;